import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LocaleSwitcher } from "../components/LocaleSwitcher";
import { confirmSignInWithNewPassword, signIn } from "../auth/session";
import { CALLER_QUERY_KEY, useCaller } from "../auth/useCaller";
import { useState } from "react";

const loginSchema = z.object({
  email: z.string().trim().email(),
  password: z.string().min(1)
});

type LoginForm = z.infer<typeof loginSchema>;

const newPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(10, { message: "min 10" })
      .regex(/[a-z]/, { message: "lower" })
      .regex(/[A-Z]/, { message: "upper" })
      .regex(/[0-9]/, { message: "digit" })
      .regex(/[^A-Za-z0-9]/, { message: "symbol" }),
    confirmPassword: z.string().min(1)
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ["confirmPassword"],
    message: "mismatch"
  });

type NewPasswordForm = z.infer<typeof newPasswordSchema>;

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { isAuthenticated, isLoading } = useCaller();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [step, setStep] = useState<"signin" | "new-password">("signin");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" }
  });

  const newPasswordForm = useForm<NewPasswordForm>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { newPassword: "", confirmPassword: "" }
  });

  if (isAuthenticated && !isLoading) {
    const dest = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (values: LoginForm) => {
    setSubmitError(null);
    try {
      const res = await signIn(values.email, values.password);
      if (res.kind === "new-password-required") {
        // First-time invited user: don't redirect, keep them on this page
        // and show the new-password form which completes activation.
        setStep("new-password");
        return;
      }
      await qc.invalidateQueries({ queryKey: CALLER_QUERY_KEY });
      const dest = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";
      navigate(dest, { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t("login.genericError"));
    }
  };

  const onSubmitNewPassword = async (values: NewPasswordForm) => {
    setSubmitError(null);
    try {
      await confirmSignInWithNewPassword(values.newPassword);
      await qc.invalidateQueries({ queryKey: CALLER_QUERY_KEY });
      const dest = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";
      navigate(dest, { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t("login.genericError"));
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6">
        {step === "signin" ? (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm"
          >
            <div className="text-center">
              <h1 className="text-2xl font-bold text-primary">DojoMatch</h1>
              <p className="text-sm text-muted-foreground">{t("login.title")}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="email">
                {t("auth.email")}
              </label>
              <Input id="email" type="email" autoComplete="email" {...register("email")} />
              {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="password">
                {t("auth.password")}
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register("password")}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            {submitError && <p className="text-sm text-destructive">{submitError}</p>}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t("common.loading") : t("auth.signIn")}
            </Button>
            <p className="text-center text-sm">
              <Link to="/forgot-password" className="text-primary hover:underline">
                {t("auth.forgot.link")}
              </Link>
            </p>
          </form>
        ) : (
          <form
            onSubmit={newPasswordForm.handleSubmit(onSubmitNewPassword)}
            className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm"
          >
            <div className="text-center">
              <h1 className="text-2xl font-bold text-primary">DojoMatch</h1>
              <p className="text-sm text-muted-foreground">{t("login.activateTitle")}</p>
            </div>
            <p className="text-sm text-muted-foreground">{t("login.activateHint")}</p>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="newPassword">
                {t("login.newPassword")}
              </label>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                {...newPasswordForm.register("newPassword")}
              />
              {newPasswordForm.formState.errors.newPassword && (
                <p className="text-sm text-destructive">
                  {t("login.passwordRequirements")}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="confirmPassword">
                {t("login.confirmPassword")}
              </label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...newPasswordForm.register("confirmPassword")}
              />
              {newPasswordForm.formState.errors.confirmPassword && (
                <p className="text-sm text-destructive">{t("login.passwordsMismatch")}</p>
              )}
            </div>

            <p className="text-xs text-muted-foreground">{t("login.passwordRequirements")}</p>

            {submitError && <p className="text-sm text-destructive">{submitError}</p>}
            <Button
              type="submit"
              className="w-full"
              disabled={newPasswordForm.formState.isSubmitting}
            >
              {newPasswordForm.formState.isSubmitting
                ? t("common.loading")
                : t("login.activateSubmit")}
            </Button>
          </form>
        )}
      </main>
      <footer className="border-t border-border px-4 sm:px-6 py-4 flex items-center justify-end text-sm text-muted-foreground">
        <LocaleSwitcher />
      </footer>
    </div>
  );
}
